import { CircleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  FieldError,
  Input,
  Label,
  Text,
  TextArea,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';
import { useI18n } from '../i18n/I18nProvider';
import { cx } from './cx';
import styles from './Field.module.css';

export interface FieldChromeProps {
  label: ReactNode;
  description?: ReactNode;
  /** Server or format error for this field; the field is marked invalid while it is set. */
  errorMessage?: string | undefined;
  /** Shows the required marker (the server decides; this is a hint, not validation). */
  isRequired?: boolean;
  /** Shows an "Optional" tag next to the label. */
  isOptional?: boolean;
  /** Field name used by error summaries to move focus here (data-field). */
  fieldName?: string;
}

export function FieldLabel({
  label,
  isRequired,
  isOptional,
}: Pick<FieldChromeProps, 'label' | 'isRequired' | 'isOptional'>) {
  const { t } = useI18n();
  return (
    <Label className={styles.label}>
      <span>{label}</span>
      {isRequired ? (
        <span className={styles.requiredMark} aria-hidden="true">
          *
        </span>
      ) : null}
      {isOptional ? <span className={styles.optionalMark}>{t.common.optional}</span> : null}
    </Label>
  );
}

export function FieldHelp({
  description,
  errorMessage,
}: Pick<FieldChromeProps, 'description' | 'errorMessage'>) {
  return (
    <>
      {description ? (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      ) : null}
      <FieldError className={styles.error}>
        <CircleAlert size={14} aria-hidden="true" />
        <span>{errorMessage}</span>
      </FieldError>
    </>
  );
}

export interface TextFieldProps
  extends
    FieldChromeProps,
    Pick<
      AriaTextFieldProps,
      | 'type'
      | 'autoComplete'
      | 'inputMode'
      | 'isDisabled'
      | 'isReadOnly'
      | 'maxLength'
      | 'autoFocus'
      | 'onBlur'
      | 'name'
    > {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Latin-only content (emails, codes) stays left-to-right inside RTL pages. */
  dir?: 'ltr' | 'rtl' | 'auto';
  multiline?: boolean;
  rows?: number;
  className?: string;
  inputClassName?: string;
  /** Text shown inside the control after the input (units, currency). */
  suffix?: ReactNode;
  'aria-label'?: string;
}

export function TextField({
  label,
  description,
  errorMessage,
  isRequired,
  isOptional,
  fieldName,
  value,
  onChange,
  placeholder,
  dir,
  multiline,
  rows,
  className,
  inputClassName,
  suffix,
  ...props
}: TextFieldProps) {
  return (
    <AriaTextField
      {...props}
      value={value}
      onChange={onChange}
      isInvalid={Boolean(errorMessage)}
      isRequired={isRequired ?? false}
      validationBehavior="aria"
      className={cx(styles.field, className)}
      data-field={fieldName}
    >
      <FieldLabel label={label} isRequired={isRequired ?? false} isOptional={isOptional ?? false} />
      <div className={styles.control}>
        {multiline ? (
          <TextArea
            className={cx(styles.input, inputClassName)}
            placeholder={placeholder}
            rows={rows ?? 3}
            dir={dir}
          />
        ) : (
          <Input className={cx(styles.input, inputClassName)} placeholder={placeholder} dir={dir} />
        )}
        {suffix ? <span className={styles.adornment}>{suffix}</span> : null}
      </div>
      <FieldHelp description={description} errorMessage={errorMessage} />
    </AriaTextField>
  );
}
